import Header from './components/Header'
import Footer from './components/Footer'
import MainContainer from './components/MainContainer'
import SessionsList from './components/SessionsList'
import CreateEditSession from './pages/CreateEditSession'
import SessionDetailsPage from './pages/SessionDetailsPage'
import { Routes, Route, createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import LoginVerifyPage from './pages/LoginVerifyPage'
import RequireAuth from './components/require-auth/RequireAuth'
import RequireAdmin from './components/require-admin/RequireAdmin'
import RequireOrgAdmin from './components/require-org-admin/RequireOrgAdmin'
import RequireGuest from './components/require-guest/RequireGuest'
import InitializeAuth from './components/initialize-auth/InitializeAuth'
import AuthorizeRedirectPage from './pages/AuthorizeRedirectPage'
import { ROUTES } from './routes'
import EmailConfirmationPage from './pages/EmailConfirmationPage'
import EmailConfirmedPage from './pages/EmailConfirmedPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import 'react-toastify/dist/ReactToastify.css'
import AppToastContainer from './components/toast/AppToastContainer'
import { type ReactNode } from 'react'
import ResetPasswordEmailSentPage from './pages/ResetPasswordEmailSentPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import SettingsPage from './pages/SettingsPage'
import AssistantImportPage from './pages/AssistantImportPage';
import AssistantPage from './pages/AssistantPage';
import MembersListPage from './pages/MembersListPage'
import MemberDetailsPage from './pages/MemberDetailsPage'
import CreateEditMember from './pages/CreateEditMember'
import GroupDetailsPage from './pages/GroupDetailsPage'
import CreateEditGroup from './pages/CreateEditGroup'
import BillingPage from './pages/BillingPage'
import MemberBillingPage from './pages/MemberBillingPage'
import CalendarPage from './pages/CalendarPage'
import TimeSlotsPage from './pages/TimeSlotsPage'
import AttendanceDayPage from './pages/AttendanceDayPage'
import AttendanceOccurrencePage from './pages/AttendanceOccurrencePage'
import SubscriptionPage from './pages/SubscriptionPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminFeedbackPage from './pages/AdminFeedbackPage'
import AdminReportsPage from './pages/AdminReportsPage'
import AdminAcquisitionPage from './pages/AdminAcquisitionPage'
import AdminReferralsPage from './pages/AdminReferralsPage'
import AdminReferralPartnerPage from './pages/AdminReferralPartnerPage'
import AdminUserMembersPage from './pages/AdminUserMembersPage'
import AdminUserActivityPage from './pages/AdminUserActivityPage'
import PartnerTermsPage from './pages/PartnerTermsPage'
import ReferralProgramPage from './pages/ReferralProgramPage'
import ReferralGate from './features/referrals/ReferralGate'
import AttributionGate from './features/attribution/AttributionGate'
import SubscriptionBanner from './features/subscription/SubscriptionBanner'
import AccountDeletionBanner from './components/AccountDeletionBanner'
import SampleDataBanner from './features/sample-data/components/SampleDataBanner'
import ConsentGate from './features/instructor-consent/ConsentGate'
import NameGate from './features/authentication/NameGate'
import KindChoiceGate from './features/organization/components/KindChoiceGate'
import WelcomeKindPage from './pages/WelcomeKindPage'
import InstructorLanguageSync from './features/instructors/components/InstructorLanguageSync'
import OrganizationVocabularySync from './features/organization/components/OrganizationVocabularySync'
import MemberLegalDocumentPage from './pages/MemberLegalDocumentPage'
import OrganizationSettingsPage from './pages/OrganizationSettingsPage'
import ProgrammesSettingsPage from './pages/ProgrammesSettingsPage'
import InvitationAcceptPage from './pages/InvitationAcceptPage'
import NotFoundPage from './pages/NotFoundPage'
import { ConsentPurpose } from './features/members/enums/ConsentPurpose'
import PortalHeader from './components/portal/PortalHeader'
import PortalContextOffer from './components/portal/PortalContextOffer'
import ChooseContextPage from './pages/ChooseContextPage'
import TrialRequestsPage from './pages/TrialRequestsPage'
import WebsitePage from './pages/WebsitePage'
import AbsencesPage from './pages/AbsencesPage'
import MaterialsPage from './pages/MaterialsPage'
import MessagesPage from './pages/MessagesPage'
import PortalMessagesPage from './pages/PortalMessagesPage'
import PortalLoginPage from './pages/PortalLoginPage'
import PortalAuthPage from './pages/PortalAuthPage'
import PortalHomePage from './pages/PortalHomePage'
import PortalMoneyPage from './pages/PortalMoneyPage'
import PortalLibraryPage from './pages/PortalLibraryPage'
import PortalProgressPage from './pages/PortalProgressPage'
import PortalMePage from './pages/PortalMePage'
import MandateSetupPage from './pages/MandateSetupPage'
import UnsubscribePage from './pages/UnsubscribePage'

// The login screen is a full-bleed standalone layout with its own logo and
// legal links, so the regular app chrome stays out of its way. The member
// portal (/portal/*) gets its own minimal chrome — no instructor navigation,
// search, subscription banner or footer.
const AppChrome = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isBareRoute =
    location.pathname === ROUTES.LOGIN ||
    location.pathname === ROUTES.REGISTER ||
    location.pathname === ROUTES.LOGIN_VERIFY;
  const isPortalRoute = location.pathname.startsWith(ROUTES.PORTAL);

  if (isPortalRoute) {
    return (
      <div className="flex min-h-screen flex-col">
        <PortalHeader />
        <MainContainer>
          <PortalContextOffer>{children}</PortalContextOffer>
        </MainContainer>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      {!isBareRoute && <Header />}
      {!isBareRoute && <SubscriptionBanner />}
      {!isBareRoute && <AccountDeletionBanner />}
      {!isBareRoute && <SampleDataBanner />}
      <MainContainer>{children}</MainContainer>
      {!isBareRoute && <Footer />}
    </div>
  );
};

/**
 * Everything that used to live directly inside BrowserRouter. It renders under a
 * data router (createBrowserRouter) so route-blocking hooks like useBlocker —
 * the basis of the unsaved-changes guard — are available; the descendant
 * <Routes> tree keeps working unchanged under the catch-all route.
 */
function AppShell() {

  return (
    <>
        {/* Page shell: header + main + footer in a min-height flex column so the
            footer pins to the viewport bottom on short pages, and follows the
            content on long ones. The login route renders without the chrome. */}
        <AppChrome>
          <Routes>
            <Route path={ROUTES.CALENDAR} element={
              <RequireAuth>
                <CalendarPage />
              </RequireAuth>} />
            <Route path={ROUTES.TIME_SLOTS} element={
              <RequireAuth>
                <TimeSlotsPage />
              </RequireAuth>} />
            <Route path={ROUTES.ATTENDANCE} element={
              <RequireAuth>
                <AttendanceDayPage />
              </RequireAuth>} />
            <Route path={ROUTES.ATTENDANCE_OCCURRENCE} element={
              <RequireAuth>
                <AttendanceOccurrencePage />
              </RequireAuth>} />
            <Route path={ROUTES.SESSIONS} element={
              <RequireAuth>
                <SessionsList />
              </RequireAuth>} />
            <Route path={ROUTES.SESSION_DETAILS} element={
              <RequireAuth>
                <SessionDetailsPage />
              </RequireAuth>} />
            <Route path={ROUTES.CREATE_SESSION} element={
              <RequireAuth>
                <CreateEditSession />
              </RequireAuth>} />
            <Route path={ROUTES.EDIT_SESSION} element={
              <RequireAuth>
                <CreateEditSession />
              </RequireAuth>} />
            <Route path={ROUTES.CREATE_MEMBER} element={
              <RequireAuth>
                <CreateEditMember />
              </RequireAuth>} />
            <Route path={ROUTES.EDIT_MEMBER} element={
              <RequireAuth>
                <CreateEditMember />
              </RequireAuth>} />
            <Route path={ROUTES.CREATE_GROUP} element={
              <RequireAuth>
                <CreateEditGroup />
              </RequireAuth>} />
            <Route path={ROUTES.EDIT_GROUP} element={
              <RequireAuth>
                <CreateEditGroup />
              </RequireAuth>} />
            <Route path={ROUTES.GROUP_DETAILS} element={
              <RequireAuth>
                <GroupDetailsPage />
              </RequireAuth>} />
            <Route path={ROUTES.GROUPS} element={
              <RequireAuth>
                <MembersListPage />
              </RequireAuth>} />
            <Route path={ROUTES.MEMBER_BILLING} element={
              <RequireAuth>
                <RequireOrgAdmin>
                  <MemberBillingPage />
                </RequireOrgAdmin>
              </RequireAuth>} />
            <Route path={ROUTES.MEMBER_DETAILS} element={
              <RequireAuth>
                <MemberDetailsPage />
              </RequireAuth>} />
            <Route path={ROUTES.MEMBERS} element={
              <RequireAuth>
                <MembersListPage />
              </RequireAuth>} />
            <Route path={ROUTES.ASSISTANT_IMPORT} element={
              <RequireAuth>
                <AssistantImportPage />
              </RequireAuth>} />
            <Route path={ROUTES.ASSISTANT} element={
              <RequireAuth>
                <AssistantPage />
              </RequireAuth>} />
            <Route path={ROUTES.BILLING} element={
              <RequireAuth>
                <RequireOrgAdmin>
                  <BillingPage />
                </RequireOrgAdmin>
              </RequireAuth>} />
            <Route path={ROUTES.HOME} element={
              <RequireAuth>
                <HomePage />
              </RequireAuth>} />
            <Route path={ROUTES.HOME_PAGE} element={
              <RequireAuth>
                <HomePage />
              </RequireAuth>} />
            <Route path={ROUTES.WELCOME_KIND} element={
              <RequireAuth>
                <RequireOrgAdmin>
                  <WelcomeKindPage />
                </RequireOrgAdmin>
              </RequireAuth>} />
            <Route path={ROUTES.CHOOSE_CONTEXT} element={
              <RequireAuth>
                <ChooseContextPage />
              </RequireAuth>} />
            <Route path={ROUTES.LOGIN} element={
              <RequireGuest>
                <LoginPage />
              </RequireGuest>
            } />
            <Route path={ROUTES.REGISTER} element={
              <RequireGuest>
                <LoginPage />
              </RequireGuest>
            } />
            <Route path={ROUTES.LOGIN_VERIFY} element={<LoginVerifyPage />} />
            <Route
              path={ROUTES.AUTHORIZE_REDIRECT}
              element={<AuthorizeRedirectPage />}
            />
            <Route
              path={ROUTES.EMAIL_CONFIRMATION}
              element={
                <RequireGuest>
                  <EmailConfirmationPage />
                </RequireGuest>
              }
            />
            <Route
              path={ROUTES.EMAIL_CONFIRMED}
              element={
                <RequireGuest>
                  <EmailConfirmedPage />
                </RequireGuest>
              }
            />
            <Route
              path={ROUTES.RESET_PASSWORD}
              element={
                <RequireGuest>
                  <ResetPasswordPage />
                </RequireGuest>
              }
            />
            <Route
              path={ROUTES.RESET_PASSWORD_EMAIL_SENT}
              element={
                <RequireGuest>
                  <ResetPasswordEmailSentPage />
                </RequireGuest>
              }
            />
            <Route
              path={ROUTES.SETTINGS}
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ORGANIZATION_SETTINGS}
              element={
                <RequireAuth>
                  <RequireOrgAdmin>
                    <OrganizationSettingsPage />
                  </RequireOrgAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.PROGRAMMES_SETTINGS}
              element={
                <RequireAuth>
                  <ProgrammesSettingsPage />
                </RequireAuth>
              }
            />
            {/* Public — invitation links open signed-out (like the legal pages) */}
            <Route
              path={ROUTES.INVITATION}
              element={<InvitationAcceptPage />}
            />
            <Route
              path={ROUTES.SUBSCRIPTION}
              element={
                <RequireAuth>
                  <SubscriptionPage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_USERS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminUsersPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_FEEDBACK}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminFeedbackPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_REPORTS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminReportsPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_ACQUISITION}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminAcquisitionPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_REFERRALS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminReferralsPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_USER_MEMBERS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminUserMembersPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_USER_ACTIVITY}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminUserActivityPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.ADMIN_REFERRAL_DETAILS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <AdminReferralPartnerPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
            {/* Public, tokenized — referral partners are not app users; the
                secret link token is their credential. */}
            <Route path={ROUTES.PARTNER_TERMS} element={<PartnerTermsPage />} />
            <Route
              path={ROUTES.REFERRAL_PROGRAM}
              element={
                <RequireAuth>
                  <ReferralProgramPage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.TERMS_OF_SERVICE}
              element={<TermsOfServicePage />}
            />
            <Route
              path={ROUTES.PRIVACY_POLICY}
              element={<PrivacyPolicyPage />}
            />
            {/* Public, shareable member-facing documents — instructors send these
                links to their members, who read them without logging in. */}
            <Route
              path={ROUTES.MEMBER_TERMS_OF_SERVICE}
              element={
                <MemberLegalDocumentPage
                  purpose={ConsentPurpose.TermsOfService}
                />
              }
            />
            <Route
              path={ROUTES.MEMBER_PRIVACY_POLICY}
              element={
                <MemberLegalDocumentPage
                  purpose={ConsentPurpose.PrivacyPolicy}
                />
              }
            />
            <Route path={ROUTES.TRIAL_REQUESTS} element={
              <RequireAuth>
                <TrialRequestsPage />
              </RequireAuth>} />
            <Route path={ROUTES.WEBSITE} element={
              <RequireAuth>
                <WebsitePage />
              </RequireAuth>} />
            <Route path={ROUTES.ABSENCES} element={
              <RequireAuth>
                <AbsencesPage />
              </RequireAuth>} />
            <Route path={ROUTES.MATERIALS} element={
              <RequireAuth>
                <MaterialsPage />
              </RequireAuth>} />
            <Route path={ROUTES.MESSAGES} element={
              <RequireAuth>
                <MessagesPage />
              </RequireAuth>} />
            <Route path={ROUTES.MESSAGE_THREAD} element={
              <RequireAuth>
                <MessagesPage />
              </RequireAuth>} />
            {/* Member portal: magic-link sign-in and the member's sections (Next, Money,
                Library, Progress, Messages, Me). Public routes with their own minimal chrome;
                every page redirects to the portal login when no portal session exists. */}
            <Route path={ROUTES.PORTAL_LOGIN} element={<PortalLoginPage />} />
            <Route path={ROUTES.PORTAL_AUTH} element={<PortalAuthPage />} />
            <Route path={ROUTES.PORTAL_MANDATE_SETUP} element={<MandateSetupPage />} />
            <Route path={ROUTES.PORTAL_UNSUBSCRIBE} element={<UnsubscribePage />} />
            <Route path={ROUTES.PORTAL_MONEY} element={<PortalMoneyPage />} />
            <Route path={ROUTES.PORTAL_LIBRARY} element={<PortalLibraryPage />} />
            <Route path={ROUTES.PORTAL_PROGRESS} element={<PortalProgressPage />} />
            <Route path={ROUTES.PORTAL_ME} element={<PortalMePage />} />
            <Route path={ROUTES.PORTAL_MESSAGES} element={<PortalMessagesPage />} />
            <Route path={ROUTES.PORTAL_MESSAGE_THREAD} element={<PortalMessagesPage />} />
            <Route path={ROUTES.PORTAL} element={<PortalHomePage />} />
            {/* Catch-all: unknown URLs get a real not-found page instead of a
                blank main area. Public — it renders inside the app chrome. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppChrome>

        {/* App-wide legal acceptance gate — overlays everything until the
            instructor accepts the current Terms of Service and Privacy Policy. */}
        <ConsentGate />
        <NameGate />

        {/* Keeps the instructor's profile language in step with the app display
            language, so backend-rendered emails speak it. */}
        <InstructorLanguageSync />

        {/* Loads the organization for every role and pushes its vocabulary
            (Fahrschüler, Fahrstunde …) into every translated string. */}
        <OrganizationVocabularySync />

        {/* Sends a new Owner once to "what kind of school do you run?". */}
        <KindChoiceGate />

        {/* Invisible referral plumbing: stashes ?ref=CODE and claims it right
            after sign-up (attribution + the 90-day trial). */}
        <ReferralGate />

        {/* Invisible paid-acquisition plumbing: stashes ?fbclid= and reports the
            trial start to Meta server-side right after the first sign-in. */}
        <AttributionGate />

        {/* Toast Notifications */}
        <AppToastContainer />
    </>
  )
}

const router = createBrowserRouter(
  [{ path: "*", element: <AppShell /> }],
  { future: { v7_relativeSplatPath: true } }
);

function App() {
  return (
    <InitializeAuth>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </InitializeAuth>
  )
}

export default App
